import writeXlsxFile from 'write-excel-file';

import type { InvoiceHistoryItem } from '../types/vida/table/invoiceHistoryTableType';
import { formatSubestipulanteCodNome } from './subestipulanteUtil';

export type InvoiceExcelColumnLabels = {
  invoiceMonth: string;
  contractNumber: string;
  subestipulanteName: string;
  type: string;
  dueDate: string;
  amount: string;
  status: string;
};

const DEFAULT_INVOICE_EXCEL_COLUMNS: InvoiceExcelColumnLabels = {
  invoiceMonth: 'Mês da Fatura',
  contractNumber: 'Número da Apólice',
  subestipulanteName: 'Sub-Estipulante',
  type: 'Tipo',
  dueDate: 'Data de Vencimento',
  amount: 'Valor',
  status: 'Status',
};

/** Colunas alinhadas com InvoiceHistoryTable (aba Pagamentos / Vida). */
export const VIDA_INVOICE_EXCEL_COLUMNS: InvoiceExcelColumnLabels = {
  invoiceMonth: 'Mês da Fatura',
  contractNumber: 'Número do Contrato',
  subestipulanteName: 'Sub-Estipulante',
  type: 'Tipo',
  dueDate: 'Vencimento',
  amount: 'Valor',
  status: 'Status',
};

/**
 * Sanitizes a string for use in filename
 */
const sanitizeFileName = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Generates a filename with current date
 */
const generateFileNameWithDate = (prefix: string, extension: string = 'xlsx'): string => {
  const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const sanitizedPrefix = sanitizeFileName(prefix);
  return `${sanitizedPrefix}_${currentDate}.${extension}`;
};

/**
 * Generates an XLSX file for invoices and downloads it automatically
 * @param invoices - Array of invoice data to export
 * @param formatCurrency - Function to format currency values
 * @param formatDate - Function to format dates
 * @param formatBillingMonth - Optional function to format billing month
 */
export const generateInvoicesXLSX = async (
  invoices: InvoiceHistoryItem[],
  formatCurrency: (value: number) => string,
  formatDate: (dateStr: string) => string,
  formatBillingMonth?: (dateStr: string) => string,
  columnLabels: InvoiceExcelColumnLabels = DEFAULT_INVOICE_EXCEL_COLUMNS,
): Promise<void> => {
  if (!invoices || invoices.length === 0) {
    return;
  }

  const columns = [
    { value: columnLabels.invoiceMonth, width: 15 },
    { value: columnLabels.contractNumber, width: 20 },
    { value: columnLabels.subestipulanteName, width: 30 },
    { value: columnLabels.type, width: 15 },
    { value: columnLabels.dueDate, width: 20 },
    { value: columnLabels.amount, width: 15 },
    { value: columnLabels.status, width: 15 },
  ];

  // Prepare data rows
  const data = invoices.map((invoice) => {
    const month = invoice.billingMonth
      ? (formatBillingMonth ? formatBillingMonth(invoice.billingMonth) : invoice.billingMonth)
      : (invoice.invoiceMonth || '');

    return [
      { type: String, value: month },
      { type: String, value: invoice.contractNumber || invoice.policyNumber || '' },
      { type: String, value: formatSubestipulanteCodNome(invoice.codSubestipulante, invoice.nomeSubestipulante) },
      { type: String, value: invoice.type },
      { type: String, value: formatDate(invoice.dueDate) },
      { type: String, value: formatCurrency(invoice.amount) },
      { type: String, value: invoice.status },
    ];
  });

  // Write file and trigger download
  await writeXlsxFile([columns, ...data], {
    fileName: generateFileNameWithDate('Faturas'),
  });
};

